import { gql } from 'graphql-request';
import { PaginationOutput, User } from 'src/shared/types';

export const getCustomerUsersQuery = gql`
    query getCustomerUsers($filters: UserListFilters!, $pagination: PaginationInput!) {
        users(filters: $filters, pagination: $pagination) {
            users {
                id
                cognitoId
                firstName
                lastName
                email
                phone
                phoneCountryCode
                customers {
                    customerId
                    roles
                    active
                    updatedAt
                    projects {
                        projectId
                        projectName
                        roles
                    }
                }
                tnc_consent
                status
            }
            pagination {
                total
                pageSize
                page
            }
        }
    }
`;

export type GetCustomerUsersQueryResult = {
    users: {
        users: User[];
        pagination: PaginationOutput;
    };
};

export const getCustomerUsersForSuperAdminQuery = gql`
    query getCustomerUsersForSuperAdmin($filters: AdminUserListFilters!, $pagination: PaginationInput!) {
        admin {
            users(filters: $filters, pagination: $pagination) {
                users {
                    id
                    cognitoId
                    firstName
                    lastName
                    email
                    phone
                    phoneCountryCode
                    customers {
                        customerId
                        roles
                        active
                        updatedAt
                        projects {
                            projectId
                            projectName
                            roles
                        }
                    }
                    tnc_consent
                    is_internal_user
                    status
                }
                pagination {
                    total
                    pageSize
                    page
                }
            }
        }
    }
`;

export type GetCustomerUsersForSuperAdminQueryResult = {
    admin: {
        users: {
            users: User[];
            pagination: {
                total: number;
                pageSize: number;
                page: number;
            };
        };
    };
};

export const createUserQuery = gql`
    mutation admin($input: CreateUserInput!) {
        admin {
            createUser(input: $input) {
                id
            }
        }
    }
`;

export const updateUserQuery = gql`
    mutation admin($input: UpdateUserInput!, $cognitoId: String!) {
        admin {
            updateUser(input: $input, cognitoId: $cognitoId)
        }
    }
`;

export type CreateUserQueryResult = {
    id: string;
};

export const getCustomerAdminUsersQuery = gql`
    query getCustomerAdminUsers($customerId: String!) {
        customerAdmins(customerId: $customerId) {
            id
            cognitoId
        }
    }
`;

export type GetCustomerAdminUsersQueryResult = {
    customerAdmins: {
        id: string;
        cognitoId: string;
    }[];
};

/** Single request for super admin / onboarding manager: customer admins plus a minimal user-list sample (page probe). */
export const userManagementSuperAdminOverviewQuery = gql`
    query userManagementSuperAdminOverview($customerId: String!, $pagination: PaginationInput!, $userType: UserType) {
        customerAdmins(customerId: $customerId) {
            id
            cognitoId
        }
        admin {
            users(filters: { customerId: $customerId, userType: $userType }, pagination: $pagination) {
                users {
                    id
                }
                pagination {
                    total
                    pageSize
                    page
                }
            }
        }
    }
`;

export type UserManagementSuperAdminOverviewResult = {
    customerAdmins: GetCustomerAdminUsersQueryResult['customerAdmins'];
    admin: {
        users: {
            users: Pick<User, 'id'>[];
            pagination: PaginationOutput;
        };
    };
};

export const hasCustomerAdminUserQuery = gql`
    query hasCustomerAdminUser($customerId: String!) {
        hasCustomerAdmin(customerId: $customerId)
    }
`;

export type HasCustomerAdminUserQueryResult = {
    hasCustomerAdmin: boolean;
};

export const createProjectUserQuery = gql`
    mutation createProjectUser(
        $firstName: String!
        $lastName: String!
        $email: String!
        $phone: String!
        $projects: [UserProjectAccessInput]!
        $customerId: String!
    ) {
        createProjectUser(
            firstName: $firstName
            lastName: $lastName
            email: $email
            phone: $phone
            projects: $projects
            customerId: $customerId
        ) {
            id
        }
    }
`;

export type CreateProjectUserQueryResult = {
    id: string;
};

export const checkUsernameAvailableQuery = gql`
    query checkUsernameAvailable($email: String!, $customerId: String!) {
        usernameAvailable(email: $email, customerId: $customerId)
    }
`;

export type CheckUsernameAvailableQueryResult = {
    usernameAvailable: boolean;
};

export type UpsertUserProjectsForCustomerResult = {
    users: {
        upsertProjects: boolean;
    };
};

export const upsertUserProjectsForCustomerMutation = gql`
    mutation upsertUserProjectsForCustomer($customerId: String!, $userId: Int!, $projects: [UserProjectAccessInput]!) {
        users {
            upsertProjects(customerId: $customerId, userId: $userId, projects: $projects)
        }
    }
`;

export type RemoveUserProjectsForCustomerResult = {
    users: {
        removeProjects: boolean;
    };
};

export const removeUserProjectsForCustomerMutation = gql`
    mutation removeUserProjectsForCustomer($customerId: String!, $userId: Int!, $projectIds: [String!]!) {
        users {
            removeProjects(customerId: $customerId, userId: $userId, projectIds: $projectIds)
        }
    }
`;

export const updateProjectUserQuery = gql`
    mutation updateMyOrganisationUser(
        $firstName: String!
        $lastName: String!
        $phone: String!
        $projects: [UserProjectAccessInput]!
        $customerId: String!
        $tnc_consent: Boolean!
        $cognitoId: String!
        $notify: Boolean!
    ) {
        updateMyOrganisationUser(
            firstName: $firstName
            lastName: $lastName
            phone: $phone
            projects: $projects
            customerId: $customerId
            tnc_consent: $tnc_consent
            cognitoId: $cognitoId
            notify: $notify
        )
    }
`;

export const enableProjectuserQuery = gql`
    mutation enableProjectuser($email: String!, $notify: Boolean!) {
        admin {
            enableUser(email: $email, notify: $notify)
        }
    }
`;

export const disableProjectuserQuery = gql`
    mutation disableProjectuser($email: String!, $notify: Boolean!) {
        admin {
            disableUser(email: $email, notify: $notify)
        }
    }
`;

export const resendInvitationEmailQuery = gql`
    mutation resendInvitation($email: String!, $password: String!) {
        resendInvitationEmail(email: $email, password: $password)
    }
`;

export type GetUserResult = {
    admin: {
        user: User;
    };
};

export const getUserQuery = gql`
    query admin($email: String!) {
        admin {
            user(email: $email) {
                id
                cognitoId
                firstName
                lastName
                email
                phone
                phoneCountryCode
                customers {
                    customerId
                    roles
                }
                projects {
                    projectId
                    roles
                }
                tnc_consent
                status
                is_internal_user
            }
        }
    }
`;

export const unassignUserFromOrganisationQuery = gql`
    mutation unassignUserFromOrganisation($id: Int!, $customerId: String!, $notify: Boolean!) {
        unassignUserFromOrganisation(id: $id, customerId: $customerId, notify: $notify)
    }
`;

export const reassignUserFromOrganisationQuery = gql`
    mutation reassignUserFromOrganisation($id: Int!, $customerId: String!, $notify: Boolean!) {
        reassignUserFromOrganisation(id: $id, customerId: $customerId, notify: $notify)
    }
`;

export const userManagementMatrixRolesQuery = gql`
    query userManagementMatrixRoles($input: UserManagementMatrixRolesInput!) {
        usersQueries {
            userManagementMatrixRoles(input: $input)
        }
    }
`;

export type UserManagementMatrixRolesQueryResult = {
    usersQueries: {
        userManagementMatrixRoles: string[];
    };
};

export type BulkApplyUserManagementSiteRoleMutationResult = {
    users: {
        bulkApplyUserManagementSiteRole: {
            changedCount: number;
            skippedAlreadyHadRole: number;
            skippedIneligible: number;
            skippedNotHadRole: number;
            changedProjectIds: string[];
        };
    };
};

export const bulkApplyUserManagementSiteRoleMutation = gql`
    mutation bulkApplyUserManagementSiteRole($input: BulkApplyUserManagementSiteRoleInput!) {
        users {
            bulkApplyUserManagementSiteRole(input: $input) {
                changedCount
                skippedAlreadyHadRole
                skippedIneligible
                skippedNotHadRole
                changedProjectIds
            }
        }
    }
`;
